import React, { memo, useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import DataTable from "react-data-table-component";
import { FiDatabase } from "react-icons/fi";
import TooltipPortal from "components/common/TooltipPortal";

function NoData({ text }) {
  return (
    <div className="flex flex-col items-center justify-center py-8 text-center">
      <div className="mb-2 rounded-full bg-slate-100 p-3 text-text-500">
        <FiDatabase size={22} />
      </div>
      <p className="text-sm font-medium text-text-500">{text}</p>
    </div>
  );
}

NoData.propTypes = {
  text: PropTypes.string.isRequired,
};

const LoadingComponent = (
  <div className="flex flex-col items-center justify-center py-10">
    <div className="h-8 w-8 animate-spin rounded-full border-2 border-slate-200 border-t-brand-900" />
    <p className="mt-3 text-sm font-medium text-text-500">Loading...</p>
  </div>
);

const defaultCustomStyles = {
  table: {
    style: {
      border: "1px solid #e2e8f0",
      borderRadius: "12px",
      overflow: "visible",
      backgroundColor: "#ffffff",
    },
  },
  tableWrapper: {
    style: {
      backgroundColor: "#ffffff",
      overflow: "visible",
    },
  },
  headRow: {
    style: {
      backgroundColor: "#f8fafc",
      borderBottom: "1px solid #e2e8f0",
      minHeight: "46px",
    },
  },
  headCells: {
    style: {
      fontSize: "12px",
      fontWeight: 700,
      textTransform: "uppercase",
      letterSpacing: "0.04em",
      color: "#627d98",
      paddingLeft: "16px",
      paddingRight: "16px",
    },
  },
  rows: {
    style: {
      minHeight: "52px",
      borderBottom: "1px solid #f1f5f9",
      color: "#334e68",
      "&:last-of-type": {
        borderBottom: "none",
      },
      "&:hover": {
        backgroundColor: "#f8fafc",
      },
    },
  },
  cells: {
    style: {
      paddingLeft: "16px",
      paddingRight: "16px",
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    },
  },
  pagination: {
    style: {
      borderTop: "1px solid #e2e8f0",
      minHeight: "56px",
    },
    pageButtonsStyle: {
      borderRadius: "8px",
    },
  },
  noData: {
    style: {
      backgroundColor: "#ffffff",
    },
  },
  progress: {
    style: {
      backgroundColor: "#ffffff",
    },
  },
};

function mergeTableStyles(base, override) {
  if (!override) return base;
  const result = { ...base };
  Object.keys(override).forEach((key) => {
    const b = base[key];
    const o = override[key];
    if (o && typeof o === "object" && !Array.isArray(o) && b && typeof b === "object") {
      const baseStyle = b.style === null || b.style === undefined ? {} : { ...b.style };
      const overrideStyle = o.style === null || o.style === undefined ? {} : { ...o.style };
      result[key] = {
        ...b,
        ...o,
        style: { ...baseStyle, ...overrideStyle },
      };
    } else {
      result[key] = o;
    }
  });
  return result;
}

function resolveColumnSelector(column) {
  if (column.selector) return column.selector;
  if (typeof column.accessor === "function") return column.accessor;
  if (typeof column.accessor === "string") {
    const key = column.accessor;
    return (row) => row[key];
  }
  return undefined;
}

function formatCellTooltipValue(value) {
  if (value == null || value === "") return "";
  if (typeof value === "object") return "";
  return String(value).trim();
}

function resolveCellTooltipText(column, row) {
  if (typeof column.tooltip === "function") return formatCellTooltipValue(column.tooltip(row));
  if (typeof column.tooltip === "string") return column.tooltip.trim();

  const selector = resolveColumnSelector(column);
  if (selector) return formatCellTooltipValue(selector(row));

  if (column.id && row[column.id] != null) {
    return formatCellTooltipValue(row[column.id]);
  }

  return "";
}

function CellWithTooltip({ children, text: tooltipText }) {
  const anchorRef = useRef(null);
  const [showTooltip, setShowTooltip] = useState(false);
  const [position, setPosition] = useState(null);
  const tip = tooltipText?.trim();

  const updateTooltipPosition = () => {
    if (!anchorRef.current) return;
    const rect = anchorRef.current.getBoundingClientRect();
    setPosition({
      left: rect.right + 10,
      top: rect.top + rect.height / 2,
    });
  };

  const handleEnter = () => {
    if (!tip) return;
    updateTooltipPosition();
    setShowTooltip(true);
  };

  const handleLeave = () => setShowTooltip(false);

  useEffect(() => {
    if (!showTooltip || !tip) return undefined;

    const handleViewportChange = () => updateTooltipPosition();
    window.addEventListener("scroll", handleViewportChange, true);
    window.addEventListener("resize", handleViewportChange);

    return () => {
      window.removeEventListener("scroll", handleViewportChange, true);
      window.removeEventListener("resize", handleViewportChange);
    };
  }, [showTooltip, tip]);

  if (!tip) {
    return <span className="block max-w-full truncate">{children}</span>;
  }

  return (
    <>
      <span
        ref={anchorRef}
        className="block max-w-full truncate"
        onMouseEnter={handleEnter}
        onMouseLeave={handleLeave}
        onFocus={handleEnter}
        onBlur={handleLeave}
      >
        {children}
      </span>
      <TooltipPortal text={tip} position={position} visible={showTooltip} />
    </>
  );
}

CellWithTooltip.propTypes = {
  children: PropTypes.node,
  text: PropTypes.string,
};

function wrapColumnCell(column, showCellTooltip) {
  const originalCell = column.cell;
  const selector = resolveColumnSelector(column);

  return (row, rowIndex, columnMeta, id) => {
    let content;

    if (originalCell) {
      content = originalCell(row, rowIndex, columnMeta, id);
    } else if (selector) {
      const value = selector(row);
      content = value == null || value === "" ? "—" : value;
    } else {
      content = "—";
    }

    if (!showCellTooltip || column.disableTooltip) return content;

    const tooltipText = resolveCellTooltipText(column, row);
    if (!tooltipText) return content;

    return <CellWithTooltip text={tooltipText}>{content}</CellWithTooltip>;
  };
}

function toRdtColumns(columns, showCellTooltip) {
  return columns.map((column) => {
    if (column.name) return column;

    const normalized = {
      ...column,
      name: column.header || column.name || "",
      sortable: column.sortable ?? false,
      grow: column.grow,
      width: column.width,
    };

    const selector = resolveColumnSelector(column);
    if (selector) {
      normalized.selector = selector;
    } else if (!normalized.cell) {
      normalized.selector = () => "";
    }

    normalized.cell = wrapColumnCell(column, showCellTooltip);

    return normalized;
  });
}

const AppDataTable = memo(
  ({
    title = "",
    columns,
    data,
    loading = false,
    onRowClicked,
    rowKey = "id",
    minWidth = 900,
    noDataText = "There are no records to display.",
    customStyles: customStylesOverride,
    className = "",
    showCellTooltip = true,
    ...rest
  }) => {
    const tableStyles = useMemo(() => {
      const merged = mergeTableStyles(defaultCustomStyles, customStylesOverride);
      const tableStyle = merged.table?.style === null || merged.table?.style === undefined ? {} : { ...merged.table.style };
      return mergeTableStyles(merged, {
        table: {
          style: {
            ...tableStyle,
            minWidth: `${minWidth}px`,
          },
        },
      });
    }, [customStylesOverride, minWidth]);
    const rdtColumns = useMemo(() => toRdtColumns(columns, showCellTooltip), [columns, showCellTooltip]);

    return (
      <div className={className}>
        {title && <h3 className="mb-3 text-base font-semibold text-text-900">{title}</h3>}
        <div className="overflow-x-auto rounded-xl">
          <DataTable
            columns={rdtColumns}
            data={data}
            keyField={rowKey}
            customStyles={tableStyles}
            progressPending={loading}
            highlightOnHover
            pointerOnHover
            persistTableHead
            onRowClicked={onRowClicked}
            noDataComponent={<NoData text={noDataText} />}
            progressComponent={LoadingComponent}
            {...rest}
          />
        </div>
      </div>
    );
  }
);

AppDataTable.propTypes = {
  title: PropTypes.string,
  columns: PropTypes.arrayOf(PropTypes.object).isRequired,
  data: PropTypes.arrayOf(PropTypes.object).isRequired,
  loading: PropTypes.bool,
  onRowClicked: PropTypes.func,
  rowKey: PropTypes.string,
  minWidth: PropTypes.number,
  noDataText: PropTypes.string,
  customStyles: PropTypes.object,
  className: PropTypes.string,
  showCellTooltip: PropTypes.bool,
};

AppDataTable.defaultProps = {
  title: "",
  loading: false,
  onRowClicked: undefined,
  rowKey: "id",
  minWidth: 900,
  noDataText: "There are no records to display.",
  customStyles: undefined,
  className: "",
  showCellTooltip: true,
};

export default AppDataTable;
